import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { payWithRazorpay } from "../lib/razorpay";
import MyPayments from "../components/MyPayments";
import { Coins, Diamond, Sparkles, Info, ArrowDown, ShoppingBag, Tv, Briefcase, Crown, Wand2, CreditCard, Loader2 } from "lucide-react";

export default function Wallet() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(null);
  const [msg, setMsg] = useState("");
  const [payRefresh, setPayRefresh] = useState(0);
  const [payErr, setPayErr] = useState("");

  useEffect(() => {
    api.get("/wallet/plans").then(r => setPlans(r.data));
    api.get("/wallet/transactions").then(r => setTxns(r.data)).catch(()=>{});
  }, []);

  const recharge = async (planId) => {
    setLoading(planId); setMsg(""); setPayErr("");
    try {
      const result = await payWithRazorpay({ kind: "goldly", plan_id: planId });
      setMsg(`✅ ${result}`);
      await refresh?.();
      api.get("/wallet/transactions").then(r => setTxns(r.data)).catch(()=>{});
      setPayRefresh(r => r + 1);
    } catch (e) { if (e.message !== "Payment cancelled") setPayErr(e.message); }
    finally { setLoading(null); }
  };

  return (
    <div className="px-4 py-6" data-testid="wallet-page">
      <h1 className="font-outfit font-extrabold text-3xl tracking-tighter mb-1">Wallet</h1>
      <p className="text-white/60 text-sm mb-5">Goldly for spending • Tokwin from gifts</p>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="rounded-3xl p-4 bg-gradient-to-br from-[#FF7A00]/15 to-[#FFA500]/5 border border-[#FF7A00]/30 neon-gold">
          <div className="flex items-center gap-1.5 text-[#FF7A00] text-xs font-bold uppercase tracking-wider"><Coins className="w-3.5 h-3.5"/>Goldly</div>
          <div className="font-outfit font-extrabold text-3xl mt-1 text-gradient-gold" data-testid="wallet-goldly">{user?.wallet_goldly?.toLocaleString() ?? 0}</div>
          <div className="text-[10px] text-white/60 mt-1">Coins • Buy → Use anywhere</div>
        </div>
        <div className="rounded-3xl p-4 bg-gradient-to-br from-[#FF2A5F]/15 to-[#FF2A5F]/5 border border-[#FF2A5F]/30">
          <div className="flex items-center gap-1.5 text-[#FF2A5F] text-xs font-bold uppercase tracking-wider"><Diamond className="w-3.5 h-3.5"/>Tokwin · Gift Earnings</div>
          <div className="font-outfit font-extrabold text-3xl mt-1 text-[#FF2A5F]" data-testid="wallet-tokwin">{user?.wallet_tokwin?.toLocaleString() ?? 0}</div>
          <div className="text-[10px] text-white/60 mt-1">Earned from live gifts you received</div>
        </div>
      </div>

      {/* Admin-controlled recharge notice */}
      <div className="mb-4 p-3 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/30 flex gap-2 text-xs" data-testid="wallet-recharge-notice">
        <Info className="w-4 h-4 text-[#FF7A00] flex-shrink-0 mt-0.5"/>
        <p className="text-white/85 leading-relaxed">
          Tap a plan → pay securely with <b className="text-[#3395FF]">Razorpay</b> (UPI, cards, netbanking). Goldly is credited to your wallet <b className="text-emerald-300">instantly</b>.
        </p>
      </div>

      {/* Spend Goldly — Templates Store */}
      <Link to="/templates" data-testid="wallet-templates-link"
        className="block mb-3 p-3 rounded-2xl bg-gradient-to-r from-[#A855F7]/20 to-[#FF7A00]/10 border border-[#A855F7]/40 active:scale-[0.99] transition">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#A855F7]/30 flex items-center justify-center flex-shrink-0">
            <ShoppingBag className="w-5 h-5 text-[#A855F7]"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">Templates Store <span className="text-[10px] text-[#FF7A00] font-normal">· spend Goldly</span></div>
            <div className="text-[11px] text-white/60">70 ready-to-use social media templates • from 50 Goldly</div>
          </div>
          <div className="text-[#A855F7] text-xs font-bold">Open →</div>
        </div>
      </Link>

      {/* AI Studio — text-to-image, image-to-image, edit */}
      <Link to="/ai-studio" data-testid="wallet-aistudio-link"
        className="block mb-3 p-3 rounded-2xl bg-gradient-to-r from-[#00E5FF]/20 to-[#A855F7]/15 border border-[#00E5FF]/40 active:scale-[0.99] transition">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#00E5FF]/25 flex items-center justify-center flex-shrink-0">
            <Wand2 className="w-5 h-5 text-[#00E5FF]"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">AI Studio <span className="text-[10px] text-[#FF7A00] font-normal">· spend Goldly</span> <span className="ml-1 text-[9px] uppercase font-extrabold text-[#FF2A5F]">New</span></div>
            <div className="text-[11px] text-white/60">Text → Image · Image → Image · Edit • from 20 Goldly</div>
          </div>
          <div className="text-[#00E5FF] text-xs font-bold">Open →</div>
        </div>
      </Link>

      {/* Quick access to Hifireels services */}
      <h3 className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-2 mt-4">Hifireels Services</h3>
      <div className="grid grid-cols-3 gap-2 mb-5" data-testid="wallet-services">
        <Link to="/drama" data-testid="wallet-svc-drama" className="p-3 rounded-2xl bg-gradient-to-br from-[#FF2A5F]/15 to-transparent border border-[#FF2A5F]/30 active:scale-95 transition">
          <Tv className="w-5 h-5 text-[#FF2A5F] mb-1"/>
          <div className="font-bold text-xs">Drama Series</div>
          <div className="text-[9px] text-white/60 mt-0.5">Vertical episodes</div>
        </Link>
        <Link to="/premium" data-testid="wallet-svc-premium" className="p-3 rounded-2xl bg-gradient-to-br from-[#FF7A00]/15 to-transparent border border-[#FF7A00]/30 active:scale-95 transition">
          <Crown className="w-5 h-5 text-[#FF7A00] mb-1"/>
          <div className="font-bold text-xs">Premium Content</div>
          <div className="text-[9px] text-white/60 mt-0.5">Unlock with Goldly</div>
        </Link>
        <Link to="/lead-gen" data-testid="wallet-svc-leadgen" className="p-3 rounded-2xl bg-gradient-to-br from-[#FF7A00]/15 to-transparent border border-[#FF7A00]/30 active:scale-95 transition">
          <Briefcase className="w-5 h-5 text-[#FF7A00] mb-1"/>
          <div className="font-bold text-xs">Lead Generation</div>
          <div className="text-[9px] text-white/60 mt-0.5">City-wise leads</div>
        </Link>
      </div>

      {payErr && <div className="mb-3 p-3 rounded-2xl bg-[#FF2A5F]/10 border border-[#FF2A5F]/30 text-[#FF2A5F] text-sm" data-testid="wallet-pay-error">{payErr}</div>}
      {msg && <div className="mb-3 p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm" data-testid="wallet-msg">{msg}</div>}

      <h2 className="font-outfit font-bold text-xl mb-3 mt-5 flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#FF7A00]"/>Recharge Goldly</h2>
      <div className="grid grid-cols-2 gap-2.5">
        {plans.map(p => (
          <button key={p.id} disabled={loading===p.id} onClick={()=>recharge(p.id)}
            data-testid={`plan-${p.id}`}
            className="rounded-2xl p-3 bg-white/5 border border-white/10 active:scale-95 transition hover:border-[#FF7A00]/50 disabled:opacity-50 text-left">
            <div className="flex items-center gap-1 text-[#FF7A00] font-bold text-lg"><Coins className="w-4 h-4"/>{p.goldly.toLocaleString()}</div>
            <div className="text-xs text-white/60 mt-0.5">for ₹{p.price_inr.toLocaleString()}</div>
            <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF2A5F]/15 text-[#FF2A5F] text-[10px] font-bold uppercase">
              {loading === p.id ? <Loader2 className="w-3 h-3 animate-spin"/> : <CreditCard className="w-3 h-3"/>}
              Pay Now
            </div>
          </button>
        ))}
      </div>

      <MyPayments refreshKey={payRefresh}/>

      {txns.length > 0 && (
        <div className="mt-6">
          <h2 className="font-outfit font-bold text-xl mb-3">Recent Transactions</h2>
          <div className="space-y-2">
            {txns.slice(0, 10).map(t => (
              <div key={t.id} className="flex items-center justify-between p-3 rounded-2xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center">
                    <ArrowDown className="w-4 h-4 text-emerald-400"/>
                  </div>
                  <div>
                    <div className="text-sm font-semibold capitalize">{t.type}</div>
                    <div className="text-[10px] text-white/50">{new Date(t.created_at).toLocaleString()}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[#FF7A00] font-bold text-sm flex items-center gap-1 justify-end"><Coins className="w-3 h-3"/>+{t.amount}</div>
                  <div className="text-[10px] text-white/50">₹{t.price_inr}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
